import axios from "./index";
import { ERROR1 } from "../constants"

const url1 = "/profile/user/security/factor/";

/**
 * Update OKTA profile, specifically for primary and secondary emails.
 * @param {string} type - The type of the email address, "primaryEmail" or "secondaryEmail".
 * @param {string} emailAddress - The email address.
 * */
const updateProfile = async ({ type, emailAddress, factorId }) => {
    try {
        await axios({
            method: "PUT",
            url: "/profile/user/profile/update",
            data: {
                factorType: type,
                factorValue: emailAddress,
                factorId: factorId
            }
        });
        return { succeed: true, message: `${type} is successfully updated` };
    } catch (err) {
        let errMsg;
        const errResponse = err?.response?.data?.errorData?.errorSummary;
        if (errResponse) {
            errMsg = errResponse;
        } else if (err.response.status === 401) {
            errMsg = err.response.data.errorMessage;
        } else {
            errMsg = ERROR1;
        }
        return {
            succeed: false,
            message: errMsg
        };
    }
};

/** Enable MFA from temp toggle **/
const enrollMFA = async () => {
    try {
        await axios({
            method: "POST",
            url: "/profile/user/security/factor/enroll/"
        });
        return {
            succeed: true,
            message:
                "You are successfully enrolled in Multi-Factor Authentiation."
        };
    } catch (err) {
        const errMsg =
            err?.response?.data?.errorData?.errorSummary ||
            ERROR1;
        return {
            succeed: false,
            message: errMsg
        };
    }
};

/**
 * Create a New MFA Factor
 * @param {string} factorType - The type of the factor, "sms", "call" or "email".
 * @param {string} factorValue - The value of the factor, such as phone number, or email address
 */
const createFactor = async ({ factorType, factorValue, verifyType, factorId }) => {
    try {
        await axios({
            method: "POST",
            url: url1,
            data: {
                profileFactor: {
                    factorValue,
                    factorType,
                    verifyType, 
                    factorId 
                },
                provider: "OKTA"
            }
        });
        return {
            succeed: true,
            message: `${factorType} factor is successfully created.`
        };
    } catch (err) {
        let errMsg;
        const errResponse = err?.response?.data?.errorData?.errorSummary;       
        if (errResponse) {
            errMsg = errResponse;
        } else if (err.response.status === 401 || err.response.status === 409) {			
            errMsg = err.response.data.errorMessage;
        } else {
            errMsg = ERROR1;
        }
        return {
            succeed: false,
            message: errMsg
        };
    }
};

/**
 * Activate a MFA Factor
 * @param {string} factorType - The type of the factor, "sms", "call" or "email".
 * @param {string} factorValue - The value of the factor, such as phone number, or email address
 * @param {string} activationCode - The activation code.
 * */
const activateFactor = async ({ factorType, factorValue, factorId, activationCode, verifyType, challengeId }) => {
    try {
        await axios({
            method: "POST",
            url: "/profile/user/security/factor/activate",
            data: {
                profileFactor: {
                    factorValue,
                    factorType,
                    factorId,
                    verifyType
                },
                provider: "OKTA",
                activationCode,
                challengeId
            }
        });
        return {
            succeed: true,
            message: `${factorType} factor is successfully activated.`
        };
    } catch (err) {
        let errMsg;
        const errResponse = err?.response?.data?.errorData?.errorSummary;
        if (errResponse) {
            errMsg = errResponse;
        } else if (err.response.status === 401) {
            errMsg = err.response.data.errorMessage;
        } else {
            errMsg = ERROR1;
        }
        return {
            succeed: false,
            message: errMsg
        };
    }
};

/**
 * Resend the MFA verification code.
 * @param {string} factorType - The type of the factor, "sms", "call" or "email".
 * @param {string} factorValue - The value of the factor, such as phone number, or email address
 * */
const resendFactorCode = async ({ factorType, factorId, factorValue, verifyType, challengeId }) => {
    try {
        await axios({
            method: "POST",
            url: "/profile/user/security/factor/resend",
            data: {
                profileFactor: {
                    factorValue,
                    factorId,
                    factorType, 
                    verifyType,
                    challengeId
                },
                provider: "OKTA"
            }
        });
        return {
            succeed: true,
            message: "Verification code is resent."
        };
    } catch (err) {
        const errMsg =
            err?.response?.data?.errorData?.errorSummary ||
            ERROR1;
        return {
            succeed: false,
            message: errMsg
        };
    }
};

/**
 * Update a MFA Factor.
 * @param {string} factorType - The type of the factor, "sms", "call" or "email".
 * @param {string} factorValue - The value of the factor, such as phone number, or email address
 * */
// factorId empty to force a replace of this number
const updateFactor = async ({ factorType, factorValue, verifyType, factorId }) => {
    try {
        const { data } =  await axios({
            method: "PUT",
            url: url1,
            data: {
                profileFactor: {
                    factorValue,
                    factorType, 
                    verifyType,
                    factorId
                },
                provider: "OKTA"
            }
        });
        return {
            succeed: true,
            message: `${factorType} factor is successfully updated.`,
            data
        };
    } catch (err) {
        let errMsg;
        const errResponse = err?.response?.data?.errorData?.errorSummary;
        if (errResponse) {
            errMsg = errResponse;
        } else if (err.response.status === 401) {
            errMsg = err.response.data.errorMessage;
        } else {
            errMsg = ERROR1;
        }
        return {
            succeed: false,
            message: errMsg
        };
    }
};

/**
 * Delete a MFA Factor.
 * @param {string} factorType - The type of the factor, "sms", "call" or "email".
 * @param {string} factorValue - The value of the factor, such as phone number, or email address
 * */
const deleteFactor = async ({ factorType, factorValue, factorId  }) => {
    try {
        await axios({
            method: "DELETE",
            url: url1,
            data: {
                profileFactor: {
                    factorValue,
                    factorType, 
                    factorId 
                },
                provider: "OKTA"
            }
        });
        return {
            succeed: true,
            message: `${factorType} factor is successfully deleted.`
        };
    } catch (err) {
        const errMsg =
            err?.response?.data?.errorData?.errorSummary ||
            ERROR1;
        return {
            succeed: false,
            message: errMsg
        };
    }
};

const getFactor = async (factorType) => {
    try {
        const { data } = await axios({
            method: "GET",
            url: `/profile/user/security/factor/${factorType.toUpperCase()}`
        });
        return {
            succeed: true,
            data
        };
    } catch (err) {
        const errMsg =
            err?.response?.data?.errorData?.errorSummary ||
            ERROR1;
        return {
            succeed: false,
            message: errMsg
        };
    }
};

const getSecurityProfile = async () => {
    try {
        const {
            data: {
                data: { userProfile, profileFactors }
            }
        } = await axios({
            method: "GET",
            url: "/profile/user/security/profile"
        });
        return { succeed: true, userProfile, profileFactors };
    } catch (err) {
        const errMsg =
            err?.response?.data?.errorData?.errorSummary ||
            ERROR1;
        return {
            succeed: false,
            message: errMsg
        };
    }
};

const getFeatureFlag = async () => {
    try {
        const { data } = await axios({
            method: "GET",
            url: "/profile/featureflag/user"
        });
        return data;
    } catch (err) {
        const errMsg =
            err?.response?.data?.errorData?.errorSummary ||
            ERROR1;
        return {
            succeed: false,
            message: errMsg
        };
    }
};

const getFeatureFlagHash = async () => {
    try {
        const { data } = await axios({
            method: "GET",
            url: "/profile/featureflag/user/hash"
        });
        return data;
    } catch (err) {
        console.error(err);
    }
};

const updatePassword = async (passwords) => {
    try {
        await axios({
            method: "POST",
            url: "/profile/user/security/password",
            data: passwords
        })
        return {
            succeed: true,
            message: "Your password has been successfully updated."
        };
    } catch (err) {
        let errMsg;
        const errResponse = err?.response?.data?.errorData?.errorSummary;

        if (errResponse) {
            errMsg = errResponse;
        } else if (err.response.status === 401) {
            errMsg = err.response.data.errorMessage;
        } else if (err.response.status === 500) {
            errMsg = err.response.data.errorMessage;
        } else {
            errMsg = ERROR1;
        }
        return {
            succeed: false,
            message: errMsg
        };
    }
};

const getUserSessionData = async () => {
    try {
        const { data } = await axios({
            method: "GET",
            url: "/profile/user"
        });
        return data;

    } catch (err) {
        const errMsg =
            err?.response?.data?.errorData?.errorSummary ||
            ERROR1;
        return {
            succeed: false,
            message: errMsg
        };
    }
};

const getAssistApproval = async (url) => {
    try {
        const data = await axios({
            method: "GET",
            url: url 
        });
        return {
            succeed: true,
            message: data.message
        };
    } catch (err) {
        const errMsg =
            err?.response?.data?.errorData?.errorSummary ||
            ERROR1;
        return {
            succeed: false,
            message: errMsg
        };
    }
};


const updateNotificationPhone = async (number) => {
    try {
        await axios({
            method: "PUT",
            url: "/profile/user/notification/phone",
            data: number
        });
        return {
            succeed: true,
            message:
                "You have successfully changed your Notification phone."
        };
    } catch (err) {
        const errMsg =
            err?.response?.data?.errorData?.errorSummary ||
            ERROR1;
        return {
            succeed: false,
            message: errMsg
        };
    }
};


const API = {
    updateProfile,
    enrollMFA,
    createFactor,
    activateFactor,
    resendFactorCode,
    updateFactor,
    deleteFactor,
    getSecurityProfile,
    getFeatureFlag,
    getFeatureFlagHash,
    updatePassword,
    getFactor,
    getUserSessionData,
    getAssistApproval,
    updateNotificationPhone
};

export default API;
